import React, {FC} from 'react';
import {useAuthUser} from "../components/UserState/hooks";
import {Navigate, Outlet} from "react-router-dom";

export type AuthenticatedRouteProps = {
  onlyPublic?: boolean;
  onlyAdmin?: boolean;
  /** Require this specific ability (admin always passes). */
  ability?: string;
};

const ADMIN_ABILITIES = [
  'admin',
  'manage_pages',
  'manage_events',
  'manage_courses',
  'manage_library',
  'manage_users',
  'manage_contacts',
];

const hasAnyAdminAbility = (user: ReturnType<typeof useAuthUser>): boolean => {
  if (!user) return false;
  return ADMIN_ABILITIES.some((a) => user.abilities?.includes(a));
};

const hasAbility = (user: ReturnType<typeof useAuthUser>, ability: string): boolean => {
  if (!user) return false;
  if (user.abilities?.includes('admin')) return true;
  return user.abilities?.includes(ability) ?? false;
};

const RequireAuth: FC<AuthenticatedRouteProps> =
  ({
     onlyPublic = false,
     onlyAdmin = false,
     ability,
   }) => {
    const user = useAuthUser();

    if (onlyAdmin && !hasAnyAdminAbility(user)) {
      return <Navigate to="/login" />
    }

    if (ability && !hasAbility(user, ability)) {
      // User is logged in but lacks the specific ability — bounce back to
      // the admin landing page (or home if they have no admin access).
      return <Navigate to={hasAnyAdminAbility(user) ? "/admin" : "/"} />
    }

    if (onlyPublic && user) {
      return <Navigate to="/"/>
    }
    if (!onlyPublic && !user) {
      return <Navigate to="/login"/>
    }
    return (
      <div>
        <Outlet />
      </div>
    )
  };

export default RequireAuth;
